/**
 * CLIENTELE – Performance & Mobile JS (Phase 4)
 * - Lazy image observer with fade-in
 * - Mobile bottom nav (active state, cart count sync)
 * - Full-screen mobile menu (slide-in, sub-menu accordion)
 * - Predictive search overlay (debounced Shopify API, inline images, recents)
 * - Horizontal scroll detection + fix
 * - Deferred non-critical JS loader
 */

(function () {
  'use strict';

  /* ════════════════════════════════════════════════════════════════════════
     1. LAZY IMAGE OBSERVER
     ════════════════════════════════════════════════════════════════════════ */
  function initLazyImages() {
    if (!('IntersectionObserver' in window)) {
      // Fallback: reveal all
      document.querySelectorAll('img[loading="lazy"]').forEach(function (img) {
        img.classList.add('cl-loaded');
      });
      return;
    }

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          var img = entry.target;
          img.classList.add('cl-loaded');
          io.unobserve(img);
        }
      });
    }, { rootMargin: '200px 0px' });

    document.querySelectorAll('img[loading="lazy"]').forEach(function (img) {
      if (img.complete) {
        img.classList.add('cl-loaded');
      } else {
        img.addEventListener('load', function () { img.classList.add('cl-loaded'); });
        io.observe(img);
      }
    });
  }

  /* ════════════════════════════════════════════════════════════════════════
     2. MOBILE BOTTOM NAV
     ════════════════════════════════════════════════════════════════════════ */
  function initBottomNav() {
    var nav = document.getElementById('cl-bottom-nav');
    if (!nav) return;

    // Set active state from current URL
    var path = window.location.pathname;
    nav.querySelectorAll('.cl-bottom-nav__link').forEach(function (link) {
      var href = link.getAttribute('href') || '';
      if (href === '/' && path === '/') {
        link.classList.add('is-active');
      } else if (href !== '/' && path.startsWith(href)) {
        link.classList.add('is-active');
      }
    });

    // Cart count badge
    syncCartCount();
  }

  function syncCartCount() {
    fetch('/cart.js')
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var count = cart.item_count || 0;
        document.querySelectorAll('.cl-bottom-nav__badge, .cl-cart-badge').forEach(function (el) {
          el.textContent = count > 0 ? count : '';
        });
      })
      .catch(function () {});
  }

  /* ════════════════════════════════════════════════════════════════════════
     3. FULL-SCREEN MOBILE MENU
     ════════════════════════════════════════════════════════════════════════ */
  function initMobileMenu() {
    var menu      = document.getElementById('cl-mobile-menu');
    var openBtns  = document.querySelectorAll('[data-cl-menu-open]');
    var closeBtn  = document.getElementById('cl-mobile-menu-close');

    if (!menu) return;

    function openMenu() {
      menu.classList.add('is-open');
      menu.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      // Focus first link
      var first = menu.querySelector('a, button');
      if (first) first.focus();
    }

    function closeMenu() {
      menu.classList.remove('is-open');
      menu.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
    }

    openBtns.forEach(function (btn) {
      btn.addEventListener('click', openMenu);
    });

    closeBtn && closeBtn.addEventListener('click', closeMenu);

    // Escape key
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && menu.classList.contains('is-open')) {
        closeMenu();
      }
    });

    // Sub-menu accordion
    menu.querySelectorAll('[data-cl-submenu-toggle]').forEach(function (toggle) {
      toggle.addEventListener('click', function (e) {
        e.preventDefault();
        var sub = document.getElementById(toggle.dataset.clSubmenuToggle);
        if (!sub) return;
        var isOpen = sub.classList.contains('is-open');
        // Close all others
        menu.querySelectorAll('.cl-mobile-menu__sub.is-open').forEach(function (s) {
          s.classList.remove('is-open');
        });
        if (!isOpen) sub.classList.add('is-open');
      });
    });
  }

  /* ════════════════════════════════════════════════════════════════════════
     4. PREDICTIVE SEARCH OVERLAY
     ════════════════════════════════════════════════════════════════════════ */
  var RECENT_SEARCHES_KEY = 'cl_recent_searches';
  var MAX_RECENT = 5;
  var TRENDING_TERMS = ['ESSENTIALS', 'COACHES JACKET', 'TRAIL PANT', 'CARGO SHORT', 'HOODIE'];
  var searchDebounceTimer = null;
  var lastQuery = '';

  function initSearch() {
    var overlay    = document.getElementById('cl-search-overlay');
    var input      = document.getElementById('cl-search-input');
    var clearBtn   = document.getElementById('cl-search-clear');
    var closeBtn   = document.getElementById('cl-search-overlay-close');
    var results    = document.getElementById('cl-search-results');
    var openBtns   = document.querySelectorAll('[data-cl-search-open]');

    if (!overlay || !input) return;

    function openSearch() {
      overlay.classList.add('is-open');
      overlay.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      setTimeout(function () { input.focus(); }, 80);
      renderDefaultResults(results);
    }

    function closeSearch() {
      overlay.classList.remove('is-open');
      overlay.setAttribute('aria-hidden', 'true');
      document.body.style.overflow = '';
      input.value = '';
      clearBtn && clearBtn.classList.remove('is-visible');
      lastQuery = '';
    }

    openBtns.forEach(function (btn) {
      btn.addEventListener('click', openSearch);
    });

    // ── Hijack Dawn's header search (details-modal / summary) ──────────────
    // The header uses <details-modal><details><summary class="header__icon--search">
    // We intercept summary clicks and open our overlay instead.
    function interceptDawnSearch() {
      var dawnSummary = document.querySelector('.header__icon--search');
      if (dawnSummary) {
        dawnSummary.addEventListener('click', function (e) {
          e.preventDefault();
          e.stopPropagation();
          // Prevent Dawn's details from opening
          var details = dawnSummary.closest('details');
          if (details) details.open = false;
          openSearch();
        }, true); // capture phase
      }

      // Also intercept any header__search form direct submissions
      var headerSearchForm = document.querySelector('.header__search form');
      if (headerSearchForm) {
        headerSearchForm.addEventListener('submit', function (e) {
          e.preventDefault();
          openSearch();
        });
      }

      // Intercept all links/buttons that reference search-modal
      document.querySelectorAll('[data-modal-handle="search-modal"], .modal__toggle[aria-haspopup="dialog"]').forEach(function(el) {
        el.addEventListener('click', function(e) {
          e.preventDefault();
          e.stopPropagation();
          openSearch();
        }, true);
      });
    }

    // Run immediately and also after a short delay (for deferred elements)
    interceptDawnSearch();
    setTimeout(interceptDawnSearch, 500);
    setTimeout(interceptDawnSearch, 1500);

    closeBtn && closeBtn.addEventListener('click', closeSearch);

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && overlay.classList.contains('is-open')) closeSearch();
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        overlay.classList.contains('is-open') ? closeSearch() : openSearch();
      }
    });

    // Click outside input/results closes overlay
    overlay.addEventListener('click', function (e) {
      if (e.target === overlay) closeSearch();
    });

    // Input handler
    input.addEventListener('input', function () {
      var q = input.value.trim();
      clearBtn && clearBtn.classList.toggle('is-visible', q.length > 0);

      if (q === lastQuery) return;
      lastQuery = q;

      clearTimeout(searchDebounceTimer);

      if (q.length < 2) {
        renderDefaultResults(results);
        return;
      }

      // Show spinner
      results.innerHTML = '<div class="cl-search-spinner"><div class="cl-spinner"></div></div>';

      searchDebounceTimer = setTimeout(function () {
        fetchPredictive(q, results);
      }, 280);
    });

    // Clear button
    clearBtn && clearBtn.addEventListener('click', function () {
      input.value = '';
      input.focus();
      clearBtn.classList.remove('is-visible');
      lastQuery = '';
      renderDefaultResults(results);
    });

    // Form submit – save to recents
    var form = document.getElementById('cl-search-form');
    form && form.addEventListener('submit', function () {
      var q = input.value.trim();
      if (q) saveRecentSearch(q);
    });
  }

  function fetchPredictive(query, container) {
    var url = '/search/suggest.json?q=' + encodeURIComponent(query)
      + '&resources[type]=product&resources[limit]=5&resources[options][unavailable_products]=last'
      + '&resources[options][fields]=title,product_type,variants.title';

    fetch(url)
      .then(function (r) { return r.json(); })
      .then(function (data) {
        var products = (data.resources && data.resources.results && data.resources.results.products) || [];
        renderProductResults(query, products, container);
      })
      .catch(function () {
        container.innerHTML = '<p class="cl-search-no-results">SEARCH UNAVAILABLE</p>';
      });
  }

  function renderProductResults(query, products, container) {
    var html = '';

    if (products.length > 0) {
      html += '<div class="cl-search-section">';
      html += '<p class="cl-search-section-label">PRODUCTS</p>';
      products.forEach(function (p) {
        var imgHtml = p.featured_image && p.featured_image.url
          ? '<div class="cl-search-product__img"><img src="' + p.featured_image.url + '" alt="' + escHtml(p.title) + '" width="48" height="60" loading="lazy"></div>'
          : '<div class="cl-search-product__img"></div>';
        var price = p.price_min ? formatMoney(p.price_min) : '';
        html += '<a class="cl-search-product" href="' + p.url + '">';
        html += imgHtml;
        html += '<div class="cl-search-product__info">';
        html += '<div class="cl-search-product__title">' + highlightQuery(escHtml(p.title.toUpperCase()), query) + '</div>';
        if (price) html += '<div class="cl-search-product__price">' + price + '</div>';
        html += '</div></a>';
      });
      html += '</div>';
    } else {
      html += '<p class="cl-search-no-results">NO RESULTS FOR "' + escHtml(query.toUpperCase()) + '"</p>';
    }

    container.innerHTML = html;
    saveRecentSearch(query);

    // Re-observe new lazy images
    container.querySelectorAll('img[loading="lazy"]').forEach(function (img) {
      if (img.complete) { img.classList.add('cl-loaded'); }
      else { img.addEventListener('load', function () { img.classList.add('cl-loaded'); }); }
    });
  }

  function renderDefaultResults(container) {
    var recents = getRecentSearches();
    var html = '';

    if (recents.length > 0) {
      html += '<div class="cl-search-section">';
      html += '<p class="cl-search-section-label">RECENT</p>';
      html += '<div class="cl-search-tags">';
      recents.forEach(function (term) {
        html += '<a class="cl-search-tag" href="/search?q=' + encodeURIComponent(term) + '">' + escHtml(term.toUpperCase()) + '</a>';
      });
      html += '</div></div>';
    }

    html += '<div class="cl-search-section">';
    html += '<p class="cl-search-section-label">TRENDING</p>';
    html += '<div class="cl-search-tags">';
    TRENDING_TERMS.forEach(function (term) {
      html += '<a class="cl-search-tag" href="/search?q=' + encodeURIComponent(term) + '">' + escHtml(term) + '</a>';
    });
    html += '</div></div>';

    container.innerHTML = html;
  }

  // Recent searches helpers
  function getRecentSearches() {
    try {
      return JSON.parse(localStorage.getItem(RECENT_SEARCHES_KEY) || '[]');
    } catch (e) { return []; }
  }

  function saveRecentSearch(term) {
    if (!term || term.length < 2) return;
    try {
      var arr = getRecentSearches().filter(function (t) { return t.toLowerCase() !== term.toLowerCase(); });
      arr.unshift(term);
      arr = arr.slice(0, MAX_RECENT);
      localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(arr));
    } catch (e) {}
  }

  // Highlight query match in result text
  function highlightQuery(text, query) {
    if (!query) return text;
    var re = new RegExp('(' + escRegex(query) + ')', 'gi');
    return text.replace(re, '<mark style="background:rgba(255,255,255,0.15);color:inherit;padding:0 1px">$1</mark>');
  }

  function escHtml(str) {
    return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function escRegex(str) {
    return str.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  function formatMoney(dollars) {
    // Shopify search API returns price_min as decimal dollar string e.g. "250.00"
    return '$' + parseFloat(dollars).toFixed(2);
  }

  /* ════════════════════════════════════════════════════════════════════════
     5. HORIZONTAL SCROLL DETECTION & FIX
     ════════════════════════════════════════════════════════════════════════ */
  function initHorizontalScrollFix() {
    // Only run in dev or if body is wider than viewport
    function check() {
      if (document.body.scrollWidth > window.innerWidth) {
        // Find the offending element (dev tool)
        if (window.location.hostname === 'localhost' || window.location.hostname.includes('sandbox')) {
          var all = document.querySelectorAll('*');
          all.forEach(function (el) {
            if (el.offsetWidth > document.documentElement.offsetWidth) {
              el.style.overflowX = 'hidden';
            }
          });
        }
      }
    }
    window.addEventListener('resize', check);
    check();
  }

  /* ════════════════════════════════════════════════════════════════════════
     6. DEFERRED NON-CRITICAL JS LOADER
     ════════════════════════════════════════════════════════════════════════ */
  function deferNonCritical() {
    // Load any scripts marked data-cl-defer after page is idle
    var deferred = document.querySelectorAll('[data-cl-defer]');
    if (!deferred.length) return;

    function loadScript(src) {
      var s = document.createElement('script');
      s.src = src;
      s.async = true;
      document.body.appendChild(s);
    }

    if ('requestIdleCallback' in window) {
      requestIdleCallback(function () {
        deferred.forEach(function (el) { if (el.dataset.clDefer) loadScript(el.dataset.clDefer); });
      }, { timeout: 3000 });
    } else {
      setTimeout(function () {
        deferred.forEach(function (el) { if (el.dataset.clDefer) loadScript(el.dataset.clDefer); });
      }, 2000);
    }
  }

  /* ════════════════════════════════════════════════════════════════════════
     7. PERFORMANCE: RESOURCE HINTS
     ════════════════════════════════════════════════════════════════════════ */
  function addResourceHints() {
    var head = document.head;

    // DNS prefetch for Shopify CDN
    ['https://cdn.shopify.com', 'https://fonts.shopifycdn.com'].forEach(function (origin) {
      if (!document.querySelector('link[href="' + origin + '"][rel="dns-prefetch"]')) {
        var link = document.createElement('link');
        link.rel = 'dns-prefetch';
        link.href = origin;
        head.appendChild(link);
      }
    });
  }

  /* ════════════════════════════════════════════════════════════════════════
     8. CART COUNT LIVE UPDATE (PubSub)
     ════════════════════════════════════════════════════════════════════════ */
  function initCartCountSync() {
    // Listen for Shopify cart:updated event
    document.addEventListener('cart:updated', syncCartCount);

    // Also sync on page focus (tab switch back)
    document.addEventListener('visibilitychange', function () {
      if (document.visibilityState === 'visible') syncCartCount();
    });
  }

  /* ════════════════════════════════════════════════════════════════════════
     INIT
     ════════════════════════════════════════════════════════════════════════ */
  function init() {
    initLazyImages();
    initBottomNav();
    initMobileMenu();
    initSearch();
    initHorizontalScrollFix();
    deferNonCritical();
    addResourceHints();
    initCartCountSync();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
